import './coapplicantResidenceRelationshipPage.scss';

import Box from '@mui/material/Box';
import { ThemeProvider } from '@mui/material/styles';
import isEmpty from 'lodash/isEmpty';
import { ReactNode, useEffect, useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Button } from '../../../components/Button/Button';
import { Header } from '../../../components/Header/Header';
import { NavigationPane } from '../../../components/NavigationPane/NavigationPane';
import { RadioGroup } from '../../../components/RadioGroup/RadioGroup';
import { RadioOptionClickEvent } from '../../../components/RadioGroup/types/RadioOptionClickEvent';
import { isValidClickEvent } from '../../../components/utils/isValidClickEvent';
import { NO, YES, YesOrNo } from '../../../store/applicantStore/types/YesOrNo';
import useCoapplicantStore from '../../../store/coapplicantStore/useCoapplicantStore';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import useWorkflowStore from '../../../store/workflowStore/useWorkflowStore';
import { locator } from '../../../testing/e2e-locator/locator';

export function Component(): ReactNode {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { t } = useTranslation();
  const { next, previous } = useWorkflowStore();

  const [isInitialSelection, setIsInitialSelection] = useState(false);

  const {
    hasSameResidenceAsApplicant,
    clearCoapplicantCurrentResidence,
    setHasSameResidenceAsApplicant,
  } = useCoapplicantStore();

  const {
    formState: { isDirty },
    handleSubmit,
    register,
    watch,
  } = useForm({
    defaultValues: {
      hasSameResidenceAsApplicantValue: hasSameResidenceAsApplicant,
    },
  });

  const previousStep = (): void => {
    navigate(previous());
  };

  const radioOptionClicked = (event: RadioOptionClickEvent<YesOrNo>): void => {
    if (!('target' in event && 'value' in event.target)) {
      return;
    }

    const hasSameResidenceAsApplicantValue = event.target.value;

    setIsInitialSelection(
      isEmpty(hasSameResidenceAsApplicant) && isValidClickEvent(event),
    );

    setHasSameResidenceAsApplicant(hasSameResidenceAsApplicantValue);
  };

  const formSubmit = (values: FieldValues): void => {
    const { hasSameResidenceAsApplicantValue } = values;

    if (!hasSameResidenceAsApplicantValue) {
      return;
    }

    if (hasSameResidenceAsApplicantValue === NO && isDirty) {
      clearCoapplicantCurrentResidence();
    }

    navigate(next());
  };

  useEffect((): void => {
    if (isInitialSelection) {
      handleSubmit(formSubmit)();
    }
  }, [isInitialSelection]);

  return (
    <ThemeProvider theme={theme}>
      <Box
        className="coapplicant-residence-relationship-page"
        sx={theme.pageLayout}
      >
        <Header
          progress={useWorkflowStore.getState().computed.progress}
          showDrawerToggle
          onBackClick={previousStep}
        />

        <main>
          <form noValidate onSubmit={handleSubmit(formSubmit)}>
            <div className="form-content">
              <h1>{t('CoapplicantResidenceRelationshipPage.title')}</h1>
              <h2>{t('CoapplicantResidenceRelationshipPage.subtitle')}</h2>

              <div style={{ marginBottom: '3rem' }}>
                <RadioGroup
                  labelPlacement="below"
                  name="hasSameResidenceAsApplicantValue"
                  options={[
                    {
                      label: t('CoapplicantResidenceRelationshipPage.yes'),
                      value: YES,
                      testId:
                        locator.pages.coapplicantResidenceRelationship.form
                          .radioOption1,
                    },
                    {
                      label: t('CoapplicantResidenceRelationshipPage.no'),
                      value: NO,
                      testId:
                        locator.pages.coapplicantResidenceRelationship.form
                          .radioOption2,
                    },
                  ]}
                  register={register}
                  testId={
                    locator.pages.coapplicantResidenceRelationship.form
                      .radioGroupRoot
                  }
                  variant="inline"
                  watch={watch}
                  onClick={radioOptionClicked}
                />
              </div>

              <div style={{ marginBottom: '3rem' }}>
                <Button
                  data-testid={
                    locator.pages.coapplicantResidenceRelationship.form
                      .submitButton
                  }
                  disabled={isEmpty(hasSameResidenceAsApplicant)}
                  fullWidth
                  submit
                >
                  {t('CoapplicantResidenceRelationshipPage.continue')}
                </Button>
              </div>
            </div>
            <footer
              data-testid={
                locator.pages.coapplicantResidenceRelationship.footer.root
              }
            >
              {t('copyright', { year: new Date().getFullYear() })}
            </footer>
          </form>
        </main>
      </Box>

      <NavigationPane withDrawer withUserNameHeader />
    </ThemeProvider>
  );
}

Component.displayName = 'CoapplicantResidenceRelationshipPage';
